import React from 'react';
import { Box, SxProps, Theme, useTheme } from '@mui/material';
import { CardItemBone, CardItemBoneProps } from '../../molecules';

interface ExclusiveListCardBoneProps {
    /** Nombre d'éléments de liste */
    itemCount?: number;
    /** Styles personnalisés */
    sx?: SxProps<Theme>;
    /** Active l'effet de fade-out sur les derniers éléments */
    fadeOut?: boolean;
    /** Nombre d'éléments à faire fade (défaut: 3) */
    fadeItemCount?: number;
    /** Intensité du fade (0-1, défaut: 0.3 pour le dernier élément) */
    fadeMinOpacity?: number;
    /** Ajouter un overlay gradient par-dessus */
    overlayGradient?: boolean;
    /** Couleur de fond pour le gradient overlay */
    backgroundColor?: string;
    /** Espace entre les éléments de la liste */
    gap?: string;
}

interface ListCardBoneProps extends ExclusiveListCardBoneProps, CardItemBoneProps {}

export const ListCardBone: React.FunctionComponent<ListCardBoneProps> = function ({
    itemCount = 3,
    contentHeight,
    contentWidth,
    sx = {},
    fadeOut = true,
    fadeItemCount = 3,
    fadeMinOpacity = 0.3,
    overlayGradient = false,
    gap = '8px',
    backgroundColor,
}) {
    const { palette } = useTheme();
    let definitiveBackgroundColor = backgroundColor;

    if (definitiveBackgroundColor === undefined) {
        definitiveBackgroundColor = palette.background.default;
    }

    const getItemOpacity = (index: number): number => {
        if (!fadeOut) return 1;

        const fadeStartIndex = itemCount - fadeItemCount;

        // Si l'élément n'est pas dans la zone de fade
        if (index < fadeStartIndex) return 1;

        // Calculer l'opacity basée sur la position dans la zone de fade
        const fadePosition = index - fadeStartIndex;
        const fadeProgress = fadePosition / (fadeItemCount - 1);

        // Interpolation linéaire de 1 à fadeMinOpacity
        return 1 - fadeProgress * (1 - fadeMinOpacity);
    };

    return (
        <Box
            sx={{
                width: '100%',
                position: 'relative',
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap,
                }}
            >
                {Array.from({ length: itemCount }).map((_, index) => (
                    <Box
                        key={`list-item-${itemCount}-${index + 1}`}
                        sx={{
                            opacity: getItemOpacity(index),
                            transition: 'opacity 0.3s ease-in-out',
                        }}
                    >
                        <CardItemBone
                            contentHeight={contentHeight}
                            contentWidth={contentWidth}
                        />
                    </Box>
                ))}
            </Box>

            {/* 🆕 Overlay gradient optionnel */}
            {overlayGradient && fadeOut && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '120px', // Ajuste selon tes besoins
                        background: `linear-gradient(
                            to bottom,
                            transparent 0%,
                            ${definitiveBackgroundColor}40 30%,
                            ${definitiveBackgroundColor}80 70%,
                            ${definitiveBackgroundColor} 100%
                        )`,
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            )}
        </Box>
    );
};
